import { useState } from "react";
import { Loader2, ShieldAlert, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDateTime, rupiah } from "../lib/api";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import { Input } from "./ui/input";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { useAuth } from "../context/AuthContext";

export function PppReconcileButton({ dateFrom, dateTo }) {
  const { user: me } = useAuth();
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState("");
  const [sel, setSel] = useState({});
  if (me?.role !== "admin") return null;
  const load = () => { setSel({}); return api.get("/reports/wifi-rumahan/reconcile", { params: { date_from: dateFrom, date_to: dateTo } }).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e))); };
  const toggle = (id) => setSel((p) => ({ ...p, [id]: !p[id] }));
  const toggleAll = (rows) => { const all = rows.every((r) => sel[r.sale_id]); setSel((p) => { const n = { ...p }; rows.forEach((r) => { n[r.sale_id] = !all; }); return n; }); };
  const selectedOf = (rows) => rows.filter((r) => sel[r.sale_id]).map((r) => r.sale_id);
  const hapus = async (rows, isAgent) => {
    const ids = selectedOf(rows);
    if (!ids.length) { toast.error("Pilih transaksi dulu"); return; }
    if (pin.length < 4) { toast.error("Masukkan PIN Super Admin dulu"); return; }
    if (!window.confirm(`Hapus ${ids.length} transaksi PPP?${isAgent ? " Secret pelanggan terkait akan DINONAKTIFKAN di router." : ""} Tindakan ini tidak bisa dibatalkan.`)) return;
    setBusy("bulk");
    try {
      const { data: d } = await api.post("/reports/wifi-rumahan/reconcile/delete", { pin, sale_ids: ids, disable_secret: isAgent });
      toast.success(`${d.deleted} transaksi dihapus${isAgent ? `, ${d.disabled} secret dinonaktifkan` : ""}${d.skipped.length ? `, ${d.skipped.length} dilewati` : ""}`);
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };
  const BulkBar = ({ rows, isAgent, testid }) => (
    <div className="flex items-center gap-2 mb-2 flex-wrap">
      <label className="inline-flex items-center gap-1.5 text-xs cursor-pointer"><Checkbox checked={rows.length > 0 && rows.every((r) => sel[r.sale_id])} onCheckedChange={() => toggleAll(rows)} data-testid={`${testid}-select-all`} /> Pilih semua ({rows.length})</label>
      <Button size="sm" variant="destructive" className="h-7 text-xs" disabled={busy === "bulk" || !selectedOf(rows).length} onClick={() => hapus(rows, isAgent)} data-testid={`${testid}-delete`}><Trash2 className="w-3.5 h-3.5 mr-1" /> Hapus terpilih ({selectedOf(rows).length}){isAgent ? " + disable secret" : ""}</Button>
    </div>
  );
  const act = async (kind, id) => {
    if (pin.length < 4) { toast.error("Masukkan PIN Super Admin dulu"); return; }
    setBusy(id);
    try {
      const { data: d } = await api.post(`/reports/wifi-rumahan/reconcile/${kind}`, { pin, sale_id: kind === "charge" ? id : "", log_id: kind === "refund" ? id : "" });
      toast.success(kind === "charge" ? `Saldo agen ditagih ${rupiah(d.charged)}` : `Saldo agen dikembalikan ${rupiah(d.refunded)}`);
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };
  return (
    <>
      <Button variant="outline" className="text-amber-400 border-amber-500/40 hover:bg-amber-500/10" onClick={() => { setOpen(true); setData(null); load(); }} data-testid="btn-ppp-reconcile"><ShieldAlert className="w-4 h-4 mr-2" /> Rekonsiliasi Saldo PPPoE</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-5xl max-h-[85vh] overflow-y-auto" data-testid="ppp-reconcile-dialog">
          <DialogHeader>
            <DialogTitle>Rekonsiliasi Saldo PPPoE</DialogTitle>
            <DialogDescription>Periode {dateFrom} – {dateTo}. Mencocokkan setiap transaksi perpanjang PPP oleh agen dengan potongan saldo "Perpanjang PPP {"<secret>"}" dalam ±3 menit.</DialogDescription>
          </DialogHeader>
          {!data ? <p className="text-sm text-muted-foreground py-6 text-center"><Loader2 className="w-4 h-4 inline animate-spin mr-2" />Memeriksa…</p> : (
            <div className="space-y-5">
              <div className="flex items-center gap-3 flex-wrap">
                <Input type="password" inputMode="numeric" placeholder="PIN Super Admin" value={pin} onChange={(e) => setPin(e.target.value)} className="h-9 w-44 font-mono" data-testid="ppp-reconcile-pin" />
                <span className="text-xs text-muted-foreground">PIN dipakai untuk semua aksi Tagih / Refund di bawah.</span>
              </div>
              <section>
                <h3 className="text-sm font-semibold text-rose-400 mb-1" data-testid="ppp-reconcile-missing-title">Transaksi agen TANPA potongan saldo — {data.missing_charge.length} item, total {rupiah(data.total_missing)} (Anda dirugikan)</h3>
                {!data.missing_charge.length ? <p className="text-xs text-muted-foreground">Tidak ada. Semua transaksi agen sudah memotong saldo.</p> : (
                  <>
                  <BulkBar rows={data.missing_charge} isAgent testid="ppp-missing" />
                  <Table className="text-xs">
                    <TableHeader><TableRow><TableHead className="w-8"></TableHead><TableHead>Waktu</TableHead><TableHead>Cabang</TableHead><TableHead>Secret</TableHead><TableHead>Agen</TableHead><TableHead className="text-right">Harga</TableHead><TableHead className="text-right">Tagih</TableHead><TableHead className="text-right">Saldo agen</TableHead><TableHead></TableHead></TableRow></TableHeader>
                    <TableBody>
                      {data.missing_charge.map((r) => (
                        <TableRow key={r.sale_id} data-testid={`ppp-missing-${r.sale_id}`}>
                          <TableCell><Checkbox checked={!!sel[r.sale_id]} onCheckedChange={() => toggle(r.sale_id)} data-testid={`ppp-missing-check-${r.sale_id}`} /></TableCell>
                          <TableCell className="font-mono whitespace-nowrap">{fmtDateTime(r.created_at)}</TableCell>
                          <TableCell>{r.router_name}</TableCell>
                          <TableCell className="font-mono">{r.secret}</TableCell>
                          <TableCell>{r.agent_code && `[${r.agent_code}] `}{r.agent_name || r.email}<div className="text-[10px] text-muted-foreground font-mono">{r.email}</div></TableCell>
                          <TableCell className="text-right font-mono">{rupiah(r.total)}</TableCell>
                          <TableCell className="text-right font-mono text-rose-400 font-bold">{rupiah(r.charge)}</TableCell>
                          <TableCell className="text-right font-mono">{rupiah(r.balance)}</TableCell>
                          <TableCell className="text-right"><Button size="sm" variant="destructive" className="h-7 text-xs" disabled={busy === r.sale_id} onClick={() => act("charge", r.sale_id)} data-testid={`ppp-tagih-${r.sale_id}`}>{busy === r.sale_id ? "…" : "Tagih"}</Button></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  </>
                )}
              </section>
              <section>
                <h3 className="text-sm font-semibold text-sky-400 mb-1" data-testid="ppp-reconcile-admin-title">Transaksi dibuat Superadmin/Admin — {(data.admin_sales || []).length} item</h3>
                {!(data.admin_sales || []).length ? <p className="text-xs text-muted-foreground">Tidak ada transaksi PPP yang dibuat admin pada periode ini.</p> : (
                  <>
                  <BulkBar rows={data.admin_sales} isAgent={false} testid="ppp-admin" />
                  <Table className="text-xs">
                    <TableHeader><TableRow><TableHead className="w-8"></TableHead><TableHead>Waktu</TableHead><TableHead>Cabang</TableHead><TableHead>Secret</TableHead><TableHead>Dibuat oleh</TableHead><TableHead className="text-right">Harga</TableHead></TableRow></TableHeader>
                    <TableBody>
                      {data.admin_sales.map((r) => (
                        <TableRow key={r.sale_id} data-testid={`ppp-admin-${r.sale_id}`}>
                          <TableCell><Checkbox checked={!!sel[r.sale_id]} onCheckedChange={() => toggle(r.sale_id)} data-testid={`ppp-admin-check-${r.sale_id}`} /></TableCell>
                          <TableCell className="font-mono whitespace-nowrap">{fmtDateTime(r.created_at)}</TableCell>
                          <TableCell>{r.router_name}</TableCell>
                          <TableCell className="font-mono">{r.secret}</TableCell>
                          <TableCell className="font-mono text-[11px]">{r.by}</TableCell>
                          <TableCell className="text-right font-mono">{rupiah(r.total)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  </>
                )}
              </section>
              <section>
                <h3 className="text-sm font-semibold text-amber-400 mb-1" data-testid="ppp-reconcile-orphan-title">Potongan saldo TANPA transaksi — {data.orphan_charges.length} item, total {rupiah(data.total_orphan)} (agen dirugikan)</h3>
                {!data.orphan_charges.length ? <p className="text-xs text-muted-foreground">Tidak ada. Semua potongan "Perpanjang PPP" punya transaksi.</p> : (
                  <Table className="text-xs">
                    <TableHeader><TableRow><TableHead>Waktu</TableHead><TableHead>Agen</TableHead><TableHead>Keterangan</TableHead><TableHead className="text-right">Terpotong</TableHead><TableHead></TableHead></TableRow></TableHeader>
                    <TableBody>
                      {data.orphan_charges.map((r) => (
                        <TableRow key={r.log_id} data-testid={`ppp-orphan-${r.log_id}`}>
                          <TableCell className="font-mono whitespace-nowrap">{fmtDateTime(r.created_at)}</TableCell>
                          <TableCell>{r.agent_code && `[${r.agent_code}] `}{r.agent_name || r.email}<div className="text-[10px] text-muted-foreground font-mono">{r.email}</div></TableCell>
                          <TableCell>{r.note}</TableCell>
                          <TableCell className="text-right font-mono text-amber-400 font-bold">{rupiah(r.amount)}</TableCell>
                          <TableCell className="text-right"><Button size="sm" className="h-7 text-xs bg-emerald-600 hover:bg-emerald-500 text-white" disabled={busy === r.log_id} onClick={() => act("refund", r.log_id)} data-testid={`ppp-refund-${r.log_id}`}>{busy === r.log_id ? "…" : "Refund"}</Button></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </section>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
